import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, TrendingUp, Package, FileText, AlertCircle, RefreshCw, BarChart3, AlertTriangle, Bell, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';
import { useCart } from '../../contexts/CartContext';
import { formatScheduleDate } from '../../lib/scheduleDates';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [messageSummary, setMessageSummary] = useState({ unread_count: 0, has_urgent: false, urgent_messages: [] });
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);
  const { clearCart, addToCart } = useCart();

  useEffect(() => {
    loadDashboard();
    loadMessageSummary();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await clientPortalService.getDashboard();
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadMessageSummary = async () => {
    try {
      const data = await clientPortalService.getMessagesSummary();
      setMessageSummary(data);
    } catch (error) {
      console.error('Error loading message summary:', error);
    }
  };

  const handleReorder = async (order) => {
    if (!order?.items?.length) {
      toast.error('No items found in this order');
      return;
    }

    setReordering(true);
    setReorderingId(order.id);
    try {
      // Clear current cart first
      clearCart();

      // Add each item to cart
      for (const item of order.items) {
        const cartItem = {
          id: item.product_id,
          name: item.product_name,
          wholesale_price: item.unit_price,
          pack_size: item.pack_size,
          image: item.image_url || '',
        };
        for (let i = 0; i < item.quantity; i++) {
          addToCart(cartItem);
        }
      }

      toast.success(`Items from ${order.order_number} added to cart! Redirecting…`);

      // Navigate to checkout
      setTimeout(() => {
        navigate('/client/checkout');
      }, 500);
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder. Please try again.');
    } finally {
      setReordering(false);
      setReorderingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-error">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Unable to load dashboard data</p>
          <Button onClick={loadDashboard} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      delivered: 'bg-green-100 text-green-800',
      confirmed: 'bg-blue-100 text-blue-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      in_production: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-indigo-100 text-indigo-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Urgent Messages Alert */}
      {messageSummary.has_urgent && messageSummary.urgent_messages.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="urgent-alert">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 flex items-center gap-2">
                Urgent Messages
                <Badge className="bg-red-500 text-white animate-pulse">
                  {messageSummary.urgent_messages.length}
                </Badge>
              </h3>
              <div className="mt-2 space-y-1">
                {messageSummary.urgent_messages.slice(0, 3).map((msg) => (
                  <p key={msg.id} className="text-sm text-red-700">
                    • {msg.subject}
                  </p>
                ))}
              </div>
              <Link to="/client/messages">
                <Button size="sm" className="mt-3 bg-red-600 hover:bg-red-700 text-white">
                  <Bell className="h-4 w-4 mr-2" />
                  View Messages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-600 mt-1">Welcome back! Here's your account overview.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Next Delivery */}
        <Card data-testid="next-delivery-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Next Delivery</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.next_delivery.date ? (
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {new Date(dashboardData.next_delivery.date).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-xs text-gray-500 mt-1">{dashboardData.next_delivery.order_number}</p>
                <p className="text-xs text-amber-600">{dashboardData.next_delivery.time_window}</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No upcoming deliveries</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card data-testid="recent-orders-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{dashboardData.recent_orders.length}</p>
            <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
          </CardContent>
        </Card>

        {/* Open Invoices */}
        <Card data-testid="open-invoices-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Open Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{dashboardData.open_invoices.count}</p>
            <p className="text-xs text-gray-500 mt-1">
              ${dashboardData.open_invoices.total_amount.toFixed(2)} due
            </p>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Top Products</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{dashboardData.top_products.length}</p>
            <p className="text-xs text-gray-500 mt-1">Frequently ordered</p>
          </CardContent>
        </Card>
      </div>

      {/* Order Deadline Banner */}
      {dashboardData.delivery_schedule?.next_deadline && (
        <Card className={`border-l-4 ${dashboardData.delivery_schedule.next_deadline.cutoff_is_today ? 'border-l-red-500 bg-red-50' : 'border-l-amber-500 bg-amber-50'}`} data-testid="order-deadline-card">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full shrink-0 ${dashboardData.delivery_schedule.next_deadline.cutoff_is_today ? 'bg-red-100' : 'bg-amber-100'}`}>
                <Clock className={`h-5 w-5 ${dashboardData.delivery_schedule.next_deadline.cutoff_is_today ? 'text-red-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">
                    {dashboardData.delivery_schedule.next_deadline.cutoff_is_today 
                      ? 'Order Deadline Today!' 
                      : 'Upcoming Order Deadline'}
                  </h3>
                  {dashboardData.delivery_schedule.next_deadline.cutoff_is_today && (
                    <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Next delivery: <span className="font-medium">{dashboardData.delivery_schedule.next_deadline.delivery_day}, {formatScheduleDate(dashboardData.delivery_schedule.next_deadline.delivery_date, dashboardData.delivery_schedule)}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Order by: <span className="font-medium">{formatScheduleDate(dashboardData.delivery_schedule.next_deadline.cutoff_date, dashboardData.delivery_schedule)}</span>
                </p>
                {dashboardData.delivery_schedule.allowed_days?.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <span className="text-xs text-gray-500">Delivery days:</span>
                    {dashboardData.delivery_schedule.allowed_days.map(day => (
                      <Badge key={day} variant="outline" className="text-xs capitalize">{day.slice(0, 3)}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/client/shop">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid="order-now-btn">
                  <ShoppingCart className="h-4 w-4 mr-1" /> Order Now
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No delivery days warning */}
      {dashboardData.delivery_schedule && !dashboardData.delivery_schedule.next_deadline && dashboardData.delivery_schedule.allowed_days?.length === 0 && (
        <Card className="border-l-4 border-l-gray-400 bg-gray-50" data-testid="no-delivery-days-warning">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-700">Delivery Schedule Not Configured</p>
                <p className="text-sm text-gray-500">Contact your admin to set up your delivery days before placing orders.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Reorder */}
        <Card data-testid="quick-reorder-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-500" />
              Quick Reorder
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.reorderable_orders && dashboardData.reorderable_orders.length > 0 ? (
              <div className="space-y-3" data-testid="reorderable-orders-list">
                {dashboardData.reorderable_orders.map((order) => {
                  const isThisLoading = reorderingId === order.id;
                  return (
                    <div
                      key={order.id}
                      className="p-3 bg-gray-50 rounded-lg flex items-center justify-between gap-3"
                      data-testid={`reorderable-order-${order.order_number}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{order.order_number}</p>
                        <p className="text-xs text-gray-500">
                          {formatScheduleDate(order.created_at, dashboardData.delivery_schedule)}
                          {' · '}
                          {order.items_count} item{order.items_count === 1 ? '' : 's'}
                          {' · '}
                          <span className="font-medium text-gray-700">${(order.total_amount || 0).toFixed(2)}</span>
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                        onClick={() => handleReorder(order)}
                        disabled={reordering}
                        data-testid={`reorder-btn-${order.order_number}`}
                      >
                        {isThisLoading ? (
                          <>
                            <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                            Adding…
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="mr-2 h-3.5 w-3.5" />
                            Reorder
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6" data-testid="quick-reorder-empty">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No previous orders</p>
                <Link to="/client/shop">
                  <Button className="bg-amber-500 hover:bg-amber-600">Browse Products</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products-list-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-500" />
              Top Products (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.top_products.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.top_products.slice(0, 5).map((product, index) => (
                  <div 
                    key={product.product_id} 
                    className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{product.product_name}</p>
                        <p className="text-xs text-gray-500">{product.quantity} packs ordered</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-gray-600">
                      ${product.total_spent.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No order history yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders Table */}
      <Card data-testid="recent-orders-table-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Orders</CardTitle>
          <Link to="/client/orders">
            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
              View All
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {dashboardData.recent_orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 text-xs font-medium text-gray-500 uppercase">Order #</th>
                    <th className="pb-3 text-xs font-medium text-gray-500 uppercase">Delivery</th>
                    <th className="pb-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="pb-3 text-xs font-medium text-gray-500 uppercase text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dashboardData.recent_orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="py-3">
                        <Link 
                          to={`/client/orders/${order.id}`}
                          className="font-medium text-amber-600 hover:text-amber-700"
                        >
                          {order.order_number}
                        </Link>
                      </td>
                      <td className="py-3 text-sm text-gray-600">
                        {(() => { const s = order.delivery_date; return s ? new Date(typeof s === 'string' && s.length === 10 ? s + 'T12:00:00' : s).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'N/A'; })()}
                      </td>
                      <td className="py-3">
                        <Badge className={getStatusBadge(order.status)}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 text-sm font-medium text-gray-900 text-right">
                        ${order.total_amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No orders yet</p>
              <Link to="/client/shop">
                <Button className="mt-4 bg-amber-500 hover:bg-amber-600">Start Shopping</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Market Trends Section */}
      <Card data-testid="market-trends-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-amber-500" />
            What's Trending Locally
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            See what other wholesale partners are ordering this month
          </p>
        </CardHeader>
        <CardContent>
          {dashboardData.market_trends && dashboardData.market_trends.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboardData.market_trends.slice(0, 6).map((trend, index) => {
                const maxQuantity = dashboardData.market_trends[0]?.total_quantity || 1;
                const percentage = (trend.total_quantity / maxQuantity) * 100;
                
                return (
                  <div key={trend.product_id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-900 truncate">{trend.product_name}</span>
                      </div>
                      {index === 0 && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Hot</Badge>
                      )}
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {trend.total_quantity} packs ordered across {trend.order_count} orders
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No market data available yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
